import { useMutation, useQuery } from '@apollo/client';
import { Alert, Flex, Text } from '@mantine/core';
import { IconGift } from '@tabler/icons-react';
import confetti from 'canvas-confetti';
import { useEffect, useMemo } from 'react';
import { CHECK_PRIZE_STATUS, CheckPrizeStatusQuery, MARK_AS_SEEN } from './graphql';

export const PrizeAlert = () => {
	const { data, refetch } = useQuery<CheckPrizeStatusQuery>(CHECK_PRIZE_STATUS);
	const [updatePrize] = useMutation(MARK_AS_SEEN, {
		onCompleted: () => {
			refetch();
		},
	});

	const triggerConfetti = () => {
		const duration = 5 * 1000;
		const animationEnd = Date.now() + duration;
		const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

		const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

		const interval = window.setInterval(() => {
			const timeLeft = animationEnd - Date.now();

			if (timeLeft <= 0) {
				return clearInterval(interval);
			}

			const particleCount = 50 * (timeLeft / duration);
			confetti({
				...defaults,
				particleCount,
				origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
			});
			confetti({
				...defaults,
				particleCount,
				origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
			});
		}, 250);
	};

	const prize = useMemo(() => {
		if (data?.checkPrizeStatus) {
			const sorted = data.checkPrizeStatus
				.filter((a) => !a?.hidden)
				.sort(
					(a, b) => new Date(b?.createdAt ?? '').getTime() - new Date(a?.createdAt ?? '').getTime()
				);
			return sorted[0];
		}
	}, [data?.checkPrizeStatus]);

	useEffect(() => {
		if (prize && !prize.seen) {
			triggerConfetti();
			updatePrize({ variables: { input: { id: prize.id, seen: true } } });
		}
	}, [prize, updatePrize]);

	if (!data?.checkPrizeStatus || !prize) return null;

	return (
		<Alert
			variant="filled"
			withCloseButton
			onClose={() => updatePrize({ variables: { input: { id: prize.id, hidden: true } } })}
			icon={<IconGift />}
			title="Congratulations! You've won a prize!"
		>
			<Flex direction="column" gap={4}>
				<Text fw={600} size="sm">
					{prize.title}
				</Text>
				<Text size="sm">
					Thank you for recycling your soft plastics — your efforts are making a real difference!
				</Text>
				<Text size="sm">
					Please email <a href="mailto:Curby@curbcycle.com.au">Curby@curbcycle.com.au</a> for more
					information!
				</Text>
			</Flex>
		</Alert>
	);
};
